import { makeObservable, observable, action, reaction, autorun, computed } from 'mobx';
import { message } from 'antd';
import { FormStore } from '../../custom_shared/stores';
import { WorksheetsSubmissionStateDto } from '../types';
import { AccountingStatementService } from '../services';
import { Disposer } from '../../custom_shared/misc';
import { WorksheetModel } from '../models';
import WorksheetsStore from './WorksheetsStore';
import ActionStore from './ActionStore';
import SessionStore from './SessionStore';

const validationErrorMessage = 'Please complete all required fields to proceed';

export default class WorksheetsSubmissionStore extends Disposer {
    isAllWorksheetsSelected: boolean = false;
    selectiveModeEnabled: boolean = false;
    isInitializing: boolean = true;
    state: WorksheetsSubmissionStateDto | null = null;

    private statePollingInterval: ReturnType<typeof setInterval> | null = null;

    constructor(
        private readonly actionStore: ActionStore,
        private readonly sessionStore: SessionStore,
        private readonly worksheetsStore: WorksheetsStore,
        private readonly formStore: FormStore,
        private readonly service: AccountingStatementService
    ) {
        super();

        makeObservable(this, {
            isAllWorksheetsSelected: observable,
            selectiveModeEnabled: observable,
            isInitializing: observable,
            state: observable,
            setSelectiveModeEnabled: action.bound,
            setIsInitializing: action,
            setWorksheetsSubmissionState: action,
            setIsAllWorksheetsSelected: action,
            updateSelectAllState: action,
            isAnyWorksheetProcessing: computed,
            canProcessWorksheets: computed,
            worksheetsToProcess: computed,
            unlockedWorksheets: computed
        });

        this.reactions.push(
            reaction(
                () => this.state,
                state => {
                    if (!state) {
                        return;
                    }

                    Object.entries(state.worksheetsProgress).forEach(([worksheetId, worksheetsProgress]) => {
                        const worksheet = this.worksheetsStore.worksheets.find(t => t.id === worksheetId);

                        if (worksheet) {
                            worksheet.updateWorksheetProgress(worksheetsProgress);
                        }
                    });
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.isAnyWorksheetProcessing,
                isAnyWorksheetProcessing => {
                    if (isAnyWorksheetProcessing) {
                        this.startStatePollingInterval();
                    } else {
                        message.info('All worksheets have been processed. Check statuses for details.', 10);
                        this.clearStatePollingInterval();
                    }
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.sessionStore.hasErrors,
                hasErrors => {
                    if (hasErrors) {
                        this.worksheetsToProcess.forEach(worksheet => worksheet.setIsSubmitting(false));
                    }
                }
            )
        );

        this.reactions.push(
            autorun(r => {
                if (!this.sessionStore.isInitializing) {
                    this.initialize();
                    r.dispose();
                }
            })
        );
    }

    get isAnyWorksheetProcessing(): boolean {
        return this.worksheetsStore.worksheets.some(w => w.isProcessing);
    }

    get canProcessWorksheets(): boolean {
        return this.worksheetsToProcess.length > 0;
    }

    get worksheetsToProcess() {
        return this.selectiveModeEnabled
            ? this.worksheetsStore.worksheets.filter(w => w.isSelectedForSubmission)
            : this.worksheetsStore.worksheets.filter(w => w.isUnprocessed || w.isFailed);
    }

    get unlockedWorksheets() {
        return this.worksheetsStore.worksheets.filter(worksheet => !worksheet.isLocked);
    }

    setSelectiveModeEnabled(selectiveModeEnabled: boolean) {
        this.selectiveModeEnabled = selectiveModeEnabled;
    }

    setIsInitializing(isInitializing: boolean) {
        this.isInitializing = isInitializing;
    }

    setWorksheetsSubmissionState(state: WorksheetsSubmissionStateDto) {
        this.state = state;
    }

    setIsAllWorksheetsSelected(isAllWorksheetsSelected: boolean) {
        this.isAllWorksheetsSelected = isAllWorksheetsSelected;
    }

    updateSelectAllState() {
        this.setIsAllWorksheetsSelected(this.worksheetsToProcess.length === this.unlockedWorksheets.length);
    }

    async initialize() {
        try {
            this.setIsInitializing(true);
            await this.getState();
        } catch (error) {
            console.error(error);
        } finally {
            this.setIsInitializing(false);
        }
    }

    async getState() {
        try {
            const response = await this.service.getWorksheetsSubmissionState(
                this.sessionStore.sessionExtension,
                this.sessionStore.sessionId
            );

            if (response.isOk()) {
                response.map(state => this.setWorksheetsSubmissionState(state));
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to get worksheets submission state');
        }
    }

    async processSingleWorksheet(worksheet: WorksheetModel) {
        const [validation] = await this.formStore.validateForms([worksheet.id]);

        worksheet.setHasValidationError(!validation.success);

        if (!validation.success) {
            message.error(validationErrorMessage);
            return;
        }

        try {
            worksheet.setIsSubmitting(true);
            await this.actionStore.processWorksheets([worksheet.id]);
            message.success('Your worksheet is being sent to SICS. You can track progress below.', 10);
            this.getStateAndStartPolling();
        } catch (error) {
            console.error(error);
            message.error('Failed to send worksheet to SICS');
            worksheet.setIsSubmitting(false);
        }
    }

    async processMultipleWorksheets() {
        const worksheetsIds = this.worksheetsToProcess.map(w => w.id);

        if (!worksheetsIds.length) {
            message.info('Please select at least one worksheet to proceed');
            return;
        }

        const validations = await this.formStore.validateForms(worksheetsIds);

        let allValid = true;

        validations.forEach(validation => {
            if (!validation.success) {
                allValid = false;
            }

            const worksheet = this.worksheetsToProcess.find(t => t.id === validation.nodeId);

            if (worksheet) {
                worksheet.setHasValidationError(!validation.success);
            }
        });

        if (!allValid) {
            message.error(validationErrorMessage);
            return;
        }

        try {
            this.worksheetsToProcess.forEach(worksheet => worksheet.setIsSubmitting(true));
            await this.actionStore.processWorksheets(worksheetsIds);
            message.success('Your worksheets are being sent to SICS. You can track progress below.', 10);
            this.getStateAndStartPolling();
            this.worksheetsToProcess.forEach(worksheet => worksheet.setIsSelectedForSubmission(false));
            this.setIsAllWorksheetsSelected(false);
        } catch (error) {
            console.error(error);
            message.error('Failed to send worksheets to SICS');
            this.worksheetsToProcess.forEach(worksheet => worksheet.setIsSubmitting(false));
        }
    }

    dispose() {
        this.clearStatePollingInterval();
        this.disposeReactions();
    }

    private startStatePollingInterval(): void {
        if (!this.statePollingInterval) {
            this.statePollingInterval = setInterval(() => this.getState(), 5000);
        }
    }

    private async getStateAndStartPolling(): Promise<void> {
        await this.getState();
        this.startStatePollingInterval();
    }

    private clearStatePollingInterval(): void {
        if (this.statePollingInterval) {
            clearInterval(this.statePollingInterval);
            this.statePollingInterval = null;
        }
    }
}
